import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { addUserRoles, selectSetups } from "../../setupSlices";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import { Role } from "../../../auth/types/authType";
import { Save } from "@mui/icons-material";

interface Props {
  userId: string;
  allRoles: Role[];
}

/** Split PascalCase / camelCase identifiers into word tokens (e.g. `ViewCompanies` → View, Companies). */
function splitPascalCase(input: string): string[] {
  if (!input) return [];
  const spaced = input
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();
  return spaced.split(/\s+/).filter(Boolean);
}

export const UserRoles = ({ userId, allRoles }: Props) => {
  const { t } = useTranslation();
  const [privilegedRoles, setPrivilegedRoles] = useState<Role[]>([]);
  const { selectedUser, loading } = useAppSelector(selectSetups);
  const dispatch = useAppDispatch();

  const formatRoleLabel = useMemo(
    () => (displayName: string | undefined) => {
      if (!displayName) return "";
      const parts = splitPascalCase(displayName).filter(
        (part) => part !== "Employee",
      );
      if (parts.length === 0) return displayName;
      return parts
        .map((part) =>
          t(`roleWords.${part}`, { defaultValue: part.replace(/_/g, " ") }),
        )
        .join(" ");
    },
    [t],
  );

  // useEffect(() => {
  //   //dispatch(changePageTitle(`Manage User`));
  //   if (userId.length > 0) {
  //     dispatch(getUser(userId as string));
  //   }
  // }, [dispatch, userId]); //user

  const handleChange =
    (roleId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      let currentRole = privilegedRoles.find((w) => w._id === roleId);

      let userRoles = privilegedRoles.filter((ur) => ur._id !== roleId);
      if (event.target.checked) {
        currentRole = { ...currentRole, isPrivileged: true };
      } else {
        currentRole = { ...currentRole, isPrivileged: false };
      }

      userRoles?.push(currentRole);
      setPrivilegedRoles(userRoles.sort());
      // (w1, w2) => (w1._id as string) - (w2._id as string))
    };
  const CheckUnCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    let userRolesWithPriv = privilegedRoles.map((pr) => ({
      ...pr,
      isPrivileged: event.target.checked,
    }));
    setPrivilegedRoles(userRolesWithPriv);
  };

  useEffect(() => {
    if (selectedUser) {
      const userRoles = selectedUser?.roles as Role[];
      const userRolesWithPriv: Role[] = [];
      for (let index = 0; index < allRoles.length; index++) {
        const element = { ...allRoles[index] };
        if (userRoles && userRoles.find((rl) => element._id === rl._id)) {
          element.isPrivileged = true;
        } else {
          element.isPrivileged = false;
        }
        userRolesWithPriv.push(element);
      }
      setPrivilegedRoles(userRolesWithPriv);
      console.log(userRolesWithPriv);
    }
  }, [selectedUser, allRoles]);

  const SaveUserRoles = () => {
    const allSelected =
      privilegedRoles.length > 0 &&
      privilegedRoles.every((r) => r.isPrivileged);

    if (allSelected) {
      dispatch(
        addUserRoles({ ids: [userId], assignAllRoles: true }),
      );
      return;
    }

    const userRoles = privilegedRoles
      .filter((r) => r.isPrivileged)
      .map((rr) => rr._id);

    userRoles.unshift(userId);

    dispatch(addUserRoles(userRoles as string[]));
  };
  return (
    <Stack sx={{ justifyContent: "center", alignItems: "center" }}>
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              color="secondary"
              name="isAllPrivileged"
              onChange={CheckUnCheckAll}
            />
          }
          label="Select All"
        />
      </FormGroup>
      <Grid
        container
        spacing={2}
        sx={{ justifyContent: "center", alignItems: "stretch" }}
      >
        {loading === "pending"
          ? new Array(20).fill("", 0, 20).map((p, i) => (
              <Grid
                sx={{ padding: 2, height: "100%" }}
                key={i * 56789}
                item
                xs={12}
                sm={6}
                md={3}
                lg={2}
              >
                <Paper elevation={3} sx={{ padding: 2, height: "100%" }}>
                  <Stack
                    spacing={2}
                    sx={{ height: "100%" }}
                    justifyContent="space-between"
                  >
                    <Skeleton animation="wave" variant="rectangular" />
                    <Skeleton
                      animation="wave"
                      variant="circular"
                      width={30}
                      height={30}
                    />
                  </Stack>
                </Paper>
              </Grid>
            ))
          : privilegedRoles &&
            privilegedRoles.map((role) => {
              return (
                <Grid key={role._id} item xs={12} sm={6} md={3} lg={2}>
                  <Paper elevation={3} sx={{ padding: 2, height: "100%" }}>
                    <Stack
                      spacing={2}
                      sx={{ height: "100%" }}
                      justifyContent="space-between"
                    >
                      <Typography noWrap={true} title={role.displayName}>
                        {formatRoleLabel(role.displayName)}
                      </Typography>
                      <Switch
                        color="secondary"
                        checked={role.isPrivileged}
                        name="isPrivileged"
                        onChange={handleChange(role._id as string)}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
      </Grid>
      <Box component="div" pb={3} mt={3}>
        <Button
          type="submit"
          sx={{ width: "100%" }}
          color="secondary"
          variant="contained"
          onClick={SaveUserRoles}
        >
          <Save /> Assign Selected Roles to {selectedUser?.name}
        </Button>
      </Box>
    </Stack>
  );
};
